import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance';
import { Field, Formik, FormikProvider, useFormik } from 'formik';
import CustomInput from '../../../components/common/CustomInput';
import CustomFile from '../../../components/common/CustomFile';
import * as Yup from 'yup';
import CustomSelect from '../../../components/common/CustomSelect';

// Validation schema
const validationSchema = Yup.object({
  company: Yup.string().trim().required('Company Name is required'),
  size: Yup.string().trim().required('Size is required'),
  product: Yup.string().trim().required('Product is required'),
  finish: Yup.string().trim().required('Finish is required'),
  groutSize: Yup.string().trim().required('Grout size is required'),
  image: Yup.mixed().required('360 Image is required'),
  view: Yup.mixed().required('View is required'),
});

const viewOptions = [
  { label: "Full View", value: 'full' },
  { label: "Detail View", value: 'detail' }
]

const Add = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([])
  const [sizeOptions, setSizeOptions] = useState([])
  const [materialOptions, setmaterialOptions] = useState([])
  const [productOptions, setProductOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);

  const navigate = useNavigate();

  const fetchCompanyData = async () => {
    try {
      const response = await axiosAuthInstance.get('company/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setCompanyOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchSizeData = async () => {
    try {
      const response = await axiosAuthInstance.get('size/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.size,
        }))
        setSizeOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchMaterialData = async () => {
    try {
      const response = await axiosAuthInstance.get('material/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setmaterialOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchProductData = async (company, size, finish) => {
    try {
      const response = await axiosAuthInstance.get('product/dropdown', {
        company, size, finish
      });

      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }));
        setProductOptions(transformedData);
      }
    } catch (error) {
      console.log('Error fetching finish data:', error);
    }
  };

  const fetchColorData = async () => {
    try {
      const response = await axiosAuthInstance.get('color/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setColorOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchCompanyData()
    fetchSizeData()
    fetchMaterialData()
    fetchColorData()
  }, [])

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitted(true);
    try {
      const formData = new FormData();
      formData.append('company', values.company);
      formData.append('size', values.size);
      formData.append('product', values.product);
      formData.append('finish', values.finish);
      formData.append('color', values.color);
      formData.append('groutSize', values.groutSize);
      formData.append('image', values.image)
      formData.append('view', values.view)

      const response = await axiosAuthInstance.post('view360/add', formData);
      if (response.status === 200) {
        resetForm();
        navigate('/360view');
      }
    } catch (error) {
      console.error('Error adding 360View:', error);
    } finally {
      setIsSubmitted(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      company: '',
      size: '',
      product: '',
      finish: '',
      color: '',
      groutSize: '',
      view: '',
      image: null,
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    const { company, size, finish } = formik.values;
    if (company && size && finish) {
      fetchProductData(company, size, finish);
    }
  }, [formik.values.size, formik.values.finish]);

  return (
    <FormikProvider value={formik}>
      <form className="flex flex-col gap-6 p-4" onSubmit={formik.handleSubmit}>
        <div className="flex gap-4">
          <Field
            name="company"
            label="Company Name"
            placeholder="Select company"
            required
            component={CustomSelect}
            options={companyOptions}
          />
          <Field
            name="size"
            label="Size"
            placeholder="Select Size"
            required
            component={CustomSelect}
            options={sizeOptions}
          />
        </div>
        <div className="flex gap-4">
          <Field
            name="finish"
            label="Finish"
            placeholder="Select Finish"
            required
            component={CustomSelect}
            options={materialOptions}
          />
          <Field
            name="product"
            label="Select Product"
            placeholder="Select Product"
            required
            component={CustomSelect}
            options={productOptions}
          />
        </div>
        <div className="flex gap-4">
          <Field
            name="groutSize"
            label="Groute Size"
            placeholder="Enter Groute Size (0-10)"
            required
            component={CustomInput}
            type="number"
          />
          <Field
            name="color"
            label="Select Groute Color"
            placeholder="Select Groute Color"
            component={CustomSelect}
            options={colorOptions}
          />
        </div>
        <div className="flex gap-4">
          <Field
            name="view"
            label="Select View"
            placeholder="Select View"
            required
            component={CustomSelect}
            options={viewOptions}
          />
          <Field
            name="image"
            label="360 Image"
            accept="image/*"
            placeholder="Upload 360 Image"
            component={CustomFile}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitted}
            className={`bg-orange-500 text-white px-6 py-2 rounded-lg text-lg font-medium ${isSubmitted ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'
              }`}
          >
            {isSubmitted ? (
              <div className="w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent"></div>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </form>
    </FormikProvider>
  );
};

export default Add;